import React from 'react';
import {
  Container,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Paper,
  useMediaQuery,
  useTheme,
  StepContent,
  Grid,
  Button,
  makeStyles,
  Theme,
  createStyles
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '../components/loadingButton';

export enum OrderWizardStep {
  DESTINATION, DELIVERY_METHOD, CHECKOUT
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      paddingTop: theme.spacing(3)
    },
    stepper: {
      padding: 0,
      backgroundColor: 'inherit'
    }
  }),
);

export const OrderWizard = ({
  children,
  step,
  next,
  back,
  canGoNext = true,
  showButtons = true,
  loading = false
}: {
  children: any,
  step: OrderWizardStep,
  next: () => void,
  back?: () => void,
  canGoNext: boolean,
  loading?: boolean,
  showButtons?: boolean
}) => {
  const { t } = useTranslation('newCard');
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const classes = useStyles();
  const getStepProps = (currentStep: OrderWizardStep) => {
    return {
      key: currentStep,
      completed: step > currentStep,
      active: currentStep === step
    }
  }
  const getStepContent = (currentStep: OrderWizardStep) => {
    if (isXs && currentStep === step) {
      return (
        <StepContent>
          <Grid container spacing={4}>
            <Grid item>
              {children}
            </Grid>
            {getButtons()}
          </Grid>
        </StepContent>
      );
    }
    else if (isXs) return <StepContent />;
    else return null;
  }

  const getButtons = () => {
    if (!showButtons) return null;
    return (
      <Grid item xs={12} style={{marginTop:15}}>
        <Grid container spacing={2} justifyContent='flex-end'>
          <Grid item>
            <Button
              onClick={() => {
                if (back) {
                  back();
                }
              }}>
              {step === OrderWizardStep.DESTINATION ? t('CANCEL') : t('BACK')}
            </Button>
          </Grid>
          <Grid item>
            <LoadingButton
              variant='contained'
              color='primary'
              disabled={!canGoNext || loading}
              loading={loading}
              onClick={next}>
              {step === OrderWizardStep.CHECKOUT ? t('FINISH') : t('NEXT')}
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const getStepper = () => (
    <Grid container direction='column'>
      <Grid item>
        <Stepper
          orientation={isXs ? 'vertical' : 'horizontal'}
          className={classes.stepper}
          alternativeLabel={!isXs}>
          <Step {...getStepProps(OrderWizardStep.DESTINATION)}>
            <StepLabel>{t('DESTINATION')}</StepLabel>
            {getStepContent(OrderWizardStep.DESTINATION)}
          </Step>
          <Step {...getStepProps(OrderWizardStep.DELIVERY_METHOD)}>
            <StepLabel>{t('DELIVERY')}</StepLabel>
            {getStepContent(OrderWizardStep.DELIVERY_METHOD)}
          </Step>
          <Step {...getStepProps(OrderWizardStep.CHECKOUT)}>
            <StepLabel>{t('CHECKOUT')}</StepLabel>
            {getStepContent(OrderWizardStep.CHECKOUT)}
          </Step>
        </Stepper>
      </Grid>
      {!isXs &&
        <Grid item>
          <Box padding={2}>
            <Grid container direction='column' spacing={2}>
              <Grid item>
                {children}
              </Grid>
              {getButtons()}
            </Grid>
          </Box>
        </Grid>
      }
    </Grid>
  )

  return (
    <Container>
      <Typography variant='h4'>
        {t('ORDER_NEW_CARD')}
      </Typography>
      <Box marginTop={2}>
        {isXs &&
          getStepper()
        }
        {!isXs &&
          <Paper className={classes.paper}>
            {getStepper()}
          </Paper>
        }
      </Box>
    </Container>
  );
}